import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Modal from '../components/Modal';
import Avatar from '../components/Avatar';
import { projectsApi } from '../utils/api';
import { formatRelativeTime, truncateText } from '../utils/helpers';
import {
  Plus, Search, SortAsc, SortDesc, Calendar, MoreVertical,
  Settings, Trash2, FolderOpen, AlertTriangle
} from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProjects();
  }, [search, sort]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.getAll({ search, sort });
      setProjects(response.data);
    } catch (err) {
      console.error('Load projects error:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await projectsApi.create(newProject);
      setShowCreateModal(false);
      setNewProject({ name: '', description: '' });
      loadProjects();
    } catch (err) {
      console.error('Create project error:', err);
    }
  };

  const handleDeleteProject = async () => {
    if (!showDeleteModal || deleteConfirmName !== showDeleteModal.name) return;

    try {
      await projectsApi.delete(showDeleteModal.id, deleteConfirmName);
      setShowDeleteModal(null);
      setDeleteConfirmName('');
      loadProjects();
    } catch (err) {
      console.error('Delete project error:', err);
    }
  };

  const sortOptions = [
    { value: 'date_desc', label: 'Newest first', icon: Calendar },
    { value: 'date_asc', label: 'Oldest first', icon: Calendar },
    { value: 'name_asc', label: 'A-Z', icon: SortAsc },
    { value: 'name_desc', label: 'Z-A', icon: SortDesc }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New project
          </button>
        </div>

        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="input w-full sm:w-48"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        )}

        {/* Empty state */}
        {!loading && projects.length === 0 && (
          <div className="text-center py-12">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {search ? 'No results' : "You don't have any projects yet"}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {search
                ? 'Try different search criteria'
                : 'Create a new project'}
            </p>
            {!search && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create new project
              </button>
            )}
          </div>
        )}

        {/* Project grid */}
        {!loading && projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => navigate(`/project/${project.id}`)}
                onSettings={() => navigate(`/project/${project.id}?settings=true`)}
                onDelete={() => setShowDeleteModal(project)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create project"
      >
        <form onSubmit={handleCreateProject}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project name *
              </label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                className="input"
                placeholder="e.g., Marketing materials"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                className="input min-h-[100px]"
                placeholder="Project description..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Project Modal */}
      <Modal
        isOpen={!!showDeleteModal}
        onClose={() => {
          setShowDeleteModal(null);
          setDeleteConfirmName('');
        }}
        title="Delete project"
      >
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">
            This action cannot be undone! The project and all its files will be permanently deleted.
          </p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Type the project name to confirm:
          <strong className="text-gray-900 dark:text-white ml-1">
            {showDeleteModal?.name}
          </strong>
        </p>
        <input
          type="text"
          value={deleteConfirmName}
          onChange={(e) => setDeleteConfirmName(e.target.value)}
          className="input mb-4"
          placeholder="Project name"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowDeleteModal(null);
              setDeleteConfirmName('');
            }}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteProject}
            disabled={deleteConfirmName !== showDeleteModal?.name}
            className="btn btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, onClick, onSettings, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className="card card-hover p-6 relative group"
      onClick={onClick}
    >
      {/* Dropdown menu */}
      {project.is_owner && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors opacity-0 group-hover:opacity-100"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          {showMenu && (
            <div
              className="dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onSettings();
                }}
                className="dropdown-item w-full flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onDelete();
                }}
                className="dropdown-item w-full flex items-center gap-2 text-red-600 dark:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex items-start gap-4">
        <Avatar
          name={project.owner_name}
          userId={project.owner_id}
          size="lg"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {project.name}
          </h3>
          <div className="tooltip-trigger relative">
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
              {project.description || 'No description'}
            </p>
            {project.description && project.description.length > 100 && (
              <div className="tooltip bottom-full left-0 mb-2 w-64">
                {project.description}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <span>Created: {formatRelativeTime(project.created_at)}</span>
        <span>Modified: {formatRelativeTime(project.updated_at)}</span>
      </div>
    </div>
  );
};

export default DashboardPage;
